import {classificationLabel, NO_FILTERS} from './queueFilters';
import type {
  ClassificationTreeNode,
  PolicyViolationQueueSummary,
  QueueClaimSelection
} from '@/api/moderationQueue';

export type QueueGroupSelection = {
  vertical: string;
  classification: string | null;
  scope: 'own' | null;
};

// The node a concept id names, with the concept ids above it.
function locate(
  nodes: readonly ClassificationTreeNode[],
  target: string,
  ancestors: readonly string[] = []
): {node: ClassificationTreeNode; ancestors: readonly string[]} | null {
  for (const node of nodes) {
    if (node.concept_id === target) {
      return {node, ancestors};
    }

    const found = locate(
      node.children,
      target,
      node.concept_id ? [...ancestors, node.concept_id] : ancestors
    );
    if (found) {
      return found;
    }
  }

  return null;
}

// What a group has available, from the per-vertical summaries. A member inside another member's subtree
// adds nothing, and a member no summary holds (a retired vertical or node) counts as zero. Counts key on an
// item's first suggested classification, so an item also suggested under a member is not counted.
export function queueGroupCount(
  summaries: readonly PolicyViolationQueueSummary[],
  selections: readonly QueueGroupSelection[]
): number {
  let total = 0;
  for (const summary of summaries) {
    const members = selections.filter(
      (selection) => selection.vertical === summary.policy_violation
    );
    if (members.some((selection) => selection.classification === null)) {
      total += summary.available;
      continue;
    }

    const subtrees = new Set(
      members.filter((member) => member.scope === null).map((member) => member.classification)
    );
    for (const member of members) {
      const located = locate(summary.tree, member.classification!);
      const covered =
        located !== null &&
        (located.ancestors.some((id) => subtrees.has(id)) ||
          (member.scope === 'own' && subtrees.has(member.classification)));
      if (located && !covered) {
        total += member.scope === 'own' ? located.node.own : located.node.available;
      }
    }
  }

  return total;
}

export type QueueGroup = {id: string; name: string; selections: QueueGroupSelection[]};

// Each member as the queue's own filter path reads it ("Child Safety › CSAM"), for showing what a group draws from.
export function queueGroupSources(
  summaries: readonly PolicyViolationQueueSummary[],
  selections: readonly QueueGroupSelection[]
): string[] {
  return selections.map((selection) => {
    const tree = summaries.find((s) => s.policy_violation === selection.vertical)?.tree ?? [];
    const label = classificationLabel({...NO_FILTERS, ...selection}, tree).join(' › ');
    return selection.scope === 'own' ? `${label} (this level only)` : label;
  });
}

export const sameSelection = (a: QueueGroupSelection, b: QueueGroupSelection) =>
  a.vertical === b.vertical && a.classification === b.classification && a.scope === b.scope;

export const hasSelection = (
  selections: readonly QueueGroupSelection[],
  target: QueueGroupSelection
) => selections.some((selection) => sameSelection(selection, target));

export const toggleSelection = (
  selections: readonly QueueGroupSelection[],
  target: QueueGroupSelection
) =>
  hasSelection(selections, target)
    ? selections.filter((selection) => !sameSelection(selection, target))
    : [...selections, target];

export function parseQueueGroupQuery(query: unknown): QueueGroupSelection[] {
  const raw = (query as {selections?: unknown} | null)?.selections;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((entry: unknown) => {
    const {vertical, classification, scope} = (entry ?? {}) as Record<string, unknown>;
    if (typeof vertical !== 'string' || !vertical) {
      return [];
    }

    const node = typeof classification === 'string' && classification ? classification : null;
    return [
      {vertical, classification: node, scope: node && scope === 'own' ? ('own' as const) : null}
    ];
  });
}

export const toClaimSelections = (
  selections: readonly QueueGroupSelection[]
): QueueClaimSelection[] =>
  selections.map((selection) => ({
    policyViolation: selection.vertical,
    ...(selection.classification
      ? {
          classification: selection.classification,
          classificationScope: selection.scope ?? 'subtree'
        }
      : {})
  }));
